import { FunctionComponent } from "react"
import { Alert, Button, Table } from "react-bootstrap"
import Container from "react-bootstrap/Container"
import { useNavigate } from "react-router-dom"
import FilterField from "../components/FilterField"
import { RentManagerProperty, Tenant } from "../data/models.interface"
import { useGetRentManagerPropertiesQuery } from "../hooks/queries/useGetRentManagerPropertiesQuery"
import { useGetTenantsQuery } from "../hooks/queries/useGetTenantsQuery"
import useSessionStorage from "../hooks/useSessionStorage"
import useSort from "../hooks/useSort"
import { filterProperty } from "../utils/StringUtils"
import { formatTenantLeaseProperties } from "../utils/AccessControlFormatter"

interface ITenantsPage {}
const TenantsPage: FunctionComponent<ITenantsPage> = () => {
	const tenantsQuery = useGetTenantsQuery()
	const propertiesQuery = useGetRentManagerPropertiesQuery()
	const tenants = tenantsQuery.data?.tenants
	const properties = propertiesQuery.data?.rentManagerProperties
	// loading layout
	const loading = !tenants || !properties
	const error = tenantsQuery.error || propertiesQuery.error
	if (loading) return <LoadingLayout error={error} />
	// normal layout
	return (
		<Container fluid>
			<h2 className="text-center mt-4">Residents</h2>
			<TenantsTable tenants={tenants} properties={properties!} />
			<BackButton />
		</Container>
	)
}
export default TenantsPage

interface ILoadingLayout {
	error?: string
}
const LoadingLayout: FunctionComponent<ILoadingLayout> = ({ error }) => {
	return (
		<Container style={{ maxWidth: 500 }}>
			<h2 className="text-center mt-4">Residents</h2>
			{error && <Alert variant={"warning"}>{error}</Alert>}
			<div className="text-center">Loading</div>
		</Container>
	)
}

interface ITenantsTable {
	tenants: Tenant[]
	properties: RentManagerProperty[]
}
const TenantsTable: FunctionComponent<ITenantsTable> = ({ tenants, properties }) => {
	const navigate = useNavigate()
	const [nameFilter, setNameFilter] = useSessionStorage("tenants-name", "")
	const [propertyFilter, setPropertyFilter] = useSessionStorage("tenants-lease-property", "")
	const [unitFilter, setUnitFilter] = useSessionStorage("tenants-unit", "")
	const [keyFilter, setKeyFilter] = useSessionStorage("tenants-keycard", "")
	const { sortBy, sort } = useSort<Tenant & { leaseProperties: string }>("Name")
	let rows = tenants.map((tenant) => ({ ...tenant, leaseProperties: formatTenantLeaseProperties(tenant, properties) }))
	rows = filterProperty(rows, (e) => e.Name!, nameFilter)
	rows = filterProperty(rows, (e) => e.leaseProperties, propertyFilter)
	rows = filterProperty(rows, (e) => e.Leases.map((l) => l.Unit.Name).join(", "), unitFilter)
	rows = filterProperty(
		rows,
		(e) =>
			e.UserDefinedValues.filter((u) => u.Name.startsWith("Key Card"))
				.map((u) => u.Value)
				.filter((k) => !!k)
				.join(", "),
		keyFilter,
	)
	rows = sort(rows)
	return (
		<Table striped bordered hover size="sm">
			<thead>
				<tr>
					<th onClick={() => sortBy("Name")}>Name</th>
					<th onClick={() => sortBy("leaseProperties")}>Lease Properties</th>
					<th>Units</th>
					<th>Keycards</th>
				</tr>
				<tr>
					<td>
						<FilterField filter={nameFilter} setFilter={setNameFilter} placeholder="Filter Name" />
					</td>
					<td>
						<FilterField filter={propertyFilter} setFilter={setPropertyFilter} placeholder="Filter Property" />
					</td>
					<td>
						<FilterField filter={unitFilter} setFilter={setUnitFilter} placeholder="Filter Unit" />
					</td>
					<td>
						<FilterField filter={keyFilter} setFilter={setKeyFilter} placeholder="Filter Keycards" />
					</td>
				</tr>
			</thead>
			<tbody>
				{rows.map((tenant) => (
					<tr key={tenant.TenantID} onClick={() => navigate(`/tenants/${tenant.TenantID}`)}>
						<td>{tenant.Name}</td>
						<td>{tenant.leaseProperties}</td>
						<td>{tenant.Leases.map((l) => l.Unit.Name).join(", ")}</td>
						<td>
							{tenant.UserDefinedValues.filter((u) => u.Name.startsWith("Key Card"))
								.map((u) => u.Value)
								.filter((k) => !!k)
								.join(", ")}
						</td>
					</tr>
				))}
			</tbody>
		</Table>
	)
}

const BackButton: FunctionComponent = () => {
	const navigate = useNavigate()
	return (
		<Button variant="secondary" className="mb-3" onClick={() => navigate(-1)}>
			Back
		</Button>
	)
}
